import { PureObject } from "@pure-ptr/core";
import { http, restEndpoint } from "./common";

export class ContractInfo extends PureObject {
    id : number;
    docsCount = 0;
    processedDocsCount = 0
    failedDocsCount = 0;
    chatSessionsCount = 0;
    lastChatSessionId = 0;

    documents : ContractDocumentInfo[] = [];

    async fetchDocuments() {
        const json = await http.get( '/ai-api/v3/contract-info/' + this.id + '/documents' );
        return ContractDocumentInfo.array( json );
    }

    async uploadDocumenToBot(contractId,options) {
        const json = await http.post( `/ai-api/v3/contract-info/${contractId}/documents`,options );
        return json
    }

    async uploadDocumentStatus(docId) {
        const json = await http.get(`/ai-api/v1/contract-info/${docId}/documents`);
        return json
    }

    async retryFailedDocuments(contractId, documentData) {
        // Parse metadata if it's a string
        let metadata = {};
        if (documentData.metadata) {
            try {
                metadata = typeof documentData.metadata === 'string' 
                    ? JSON.parse(documentData.metadata) 
                    : documentData.metadata;
            } catch (e) {
                console.warn('Failed to parse metadata:', e);
            }
        }
        
        const payload = {
            s3_key: documentData.s3Key || documentData.s3_key,
            filename: documentData.documentName || documentData.filename,
            contract_id: contractId,
            document_id: documentData.id,
            metadata: metadata,
            description: documentData.description || ''
        };
        const json = await http.post(`/ai-api/v3/contract-info/${contractId}/documents?retry_flag=true`, payload);
        return json
    }

    async createNewChatSession(options) {
        const json = await http.post( `/ai-api/v2/chat-session`,options );
        return json
    }
     async addUserMessageToSession(sessionId,options) {
        const json = await http.post( `/ai-api/v1/chat-session/${sessionId}/messages/`,options );
        return json
    }

    async getAllSessionMessages(sessionId: string, lastMessageId?: string)  {
        let params = "";
        if (lastMessageId) {
            params += `&lastMessageId=${lastMessageId}`;
        }
        params += `&limit=200&page=0`;
        const json = await http.get( `/ai-api/v2/chat-session/${sessionId}/messages?${params}`,null,'x-ndjson');
        return json
    }
    async getAllChatSessions(id, type) {
        let queryParam = '';

        if (type === 'claim') {
            queryParam = `type=${type}&claim_analysis_details_id=${id}`;
        } else {
            queryParam = `type=${type}&contractId=${id}`;
        }

        const json = await http.get(`/ai-api/v2/chat-session?${queryParam}&limit=200&page=0`);
        // in json add new field 'serial' to each object
        json.forEach((item, index) => {
            item.serial = index + 1;
        });
        return json;
    }

    async getAICreditLimits(clientId) {
        const json = await http.get( `/api/v1/credit-accounts/usage/${clientId}`);
        return json
    }
    async deleteChatSession(session_id) {
        const json = await http.del( `/ai-api/v1/chat-session/${session_id}/messages`);
        return json
    }
     async uploadAllDocumentsToBot(contractId,options) {
        const json = await http.post( `/ai-api/v2/contract-info/${contractId}/all_documents`,options );
        return json
    }
    async addUserMessageToSessionStreamed(sessionId, options, isRetry = false) {
        const retryParam = isRetry ? '?is_retry=true' : '';
        const json = await http.post( `/ai-api/v3/chat-session/${sessionId}/messages/stream${retryParam}`, options, 'json',
        undefined,
        true );
        return json
    }
    async getSessionDetails(sessionId) {
        const json = await http.get( `/ai-api/v1/chat-session/${sessionId}`);
        return json
    }

    // update title of current chat
    async updateChatSessionTitle(sessionId, title) {
        const json = await http.patch( `/ai-api/v1/chat-session/${sessionId}/title`, { title });
        return json;
    }


    // Generate summary of claims
    async getClaimsSummary(claimId, payload={}) {
        const json = await http.post(`/ai-api/v1/claim-summary/${claimId}`,payload);
        return json;
    }

    // Streamed claim summary - returns raw fetch Response so caller can read response.body.getReader()
    async getClaimsSummaryStream(claimId, payload = {}) {
        const res = await http.post(
            `/ai-api/v1/claim-summary/stream/${claimId}`,
            payload,
            'json',
            undefined,
            true
        );
        return res;
    }


    // Generate summary of claims

    async poolSummaryDataContract(contractId) {
        const json = await http.get(`/ai-api/v1/contract-summary/status/${contractId}`);
        return json
    }

    async deleteDocuments(documentId) {
        const json = await http.del(`/api/v2/cms/document/${documentId}`);
        return json
    }

    async refreshContractAfterDocumentDeletion(contractId) {
        const json = await http.post(`/ai-api/v2/contract-info/${contractId}/refresh-after-deletion`,null);
        return json
    }

    // GuardRails integration
    async getGuardRailsAnalysis(message, ignoreGuardrails = false) {
        const json = await http.post(`/ai-api/chat/check_message_guardrail`, { 
            "body": message,
            "is_filter_search": ignoreGuardrails
        });
        return json;
    }

    // AI Claim Search - natural language query
    async searchClaims(query: string, filters?: any, significant_variance?: boolean, model?: string, confidence_score?: boolean) {
        const payload: any = { query };
        
        // Include filters if provided (filters applied before AI mode was enabled)
        if (filters) {
            payload.filters = filters;
        }
        
        // Include significant_variance flag (controls /analyses/true vs /analyses/false endpoint)
        if (significant_variance !== undefined && significant_variance !== null) {
            payload.significant_variance = significant_variance;
        }

        // Include optional model override
        if (model) {
            payload.model = model;
        }

        // Include confidence_score flag — tells the AI whether to include low-confidence claims.
        // Must only be true for super_admin users (enforced at call site).
        if (confidence_score !== undefined && confidence_score !== null) {
            payload.confidence_score = confidence_score;
        }
        
        const json = await http.post(`/ai-api/v1/claim-filters`, payload);
        return json;
    }

    // Convert filters to natural language description
    async filtersToNaturalLanguage(filter: any, sort?: any[], significant_variance?: boolean, confidence_score?: boolean) {
        const payload: any = { filter };
        
        // Include sort if provided
        if (sort && sort.length > 0) {
            payload.sort = sort;
        }
        
        // Include significant_variance flag
        if (significant_variance !== undefined && significant_variance !== null) {
            payload.significant_variance = significant_variance;
        }

        // Include confidence_score flag (super_admin only; enforced at call site)
        if (confidence_score !== undefined && confidence_score !== null) {
            payload.confidence_score = confidence_score;
        }
        
        const json = await http.post(`/ai-api/v1/filters-to-nl`, payload);
        return json;
    }
    // Stop streaming response
    async stopResponse(sessionId: number, isBlocked: boolean = false) {
        try {
            const queryParam = isBlocked ? '?blocked=true' : '';
            const json = await http.post(`/ai-api/v1/chat-session/${sessionId}/messages/stop${queryParam}`, {});
            return json;
        } catch (error) {
            console.error('Stop response API error:', error);
            return { status: 'error' };
        }
    }

    // Submit message feedback (thumbs up/down or reset)
    async submitMessageFeedback(messageId: string | number, feedbackStatus: 'THUMBS_UP' | 'THUMBS_DOWN' | null, feedbackMessage?: string | null) {
        try {
            const payload: any = {
                feedback_status: feedbackStatus
            };
            
            // Include feedback_message if provided (could be null for reset)
            if (feedbackMessage !== undefined) {
                payload.feedback_message = feedbackMessage;
            }
            
            const json = await http.patch(`/ai-api/v1/messages/${messageId}/feedback`, payload);
            return json;
        } catch (error) {
            console.error('Submit feedback API error:', error);
            throw error;
        }
    }

    // Get available LLM models
    async getAvailableModels() {
        try {
            const json = await http.get(`/ai-api/v1/models/available`);
            return json;
        } catch (error) {
            console.error('Get available models API error:', error);
            // Return empty structure as fallback
            return { explanatory: [], reasoning: [] };
        }
    }
}

export const contractInfoEndpoint = restEndpoint( ContractInfo, '/ai-api/v1/contract-info' );

export class ContractDocumentInfo extends PureObject {
    id : number;
    documentUrl = '';
    documentName = '';
    metadata = { "type": "" };
    description = '';
}

